import { Camera, FileText, RotateCcw, Trash2, Upload } from "lucide-react";
import { useRef, useState, type ChangeEvent, type FC } from "react";
import { toast } from "sonner";

import { ForeignNationalDocumentsAPI } from "@/api/foreignNationalDocumentsApi";
import { CLIENT_DOCUMENT_ACCEPT } from "@/api/types/foreignNationalDocument";
import { LoadingState } from "@/components/brand";
import { Button } from "@/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from "@/components/ui/empty";

import { ClientPortalAPI } from "../api";
import { useClientPortal } from "../components/ClientPortalShell";
import { PortalCard } from "../components/PortalParts";
import { UploadCheckCard } from "../components/UploadCheckCard";
import { usePortalT } from "../i18n";
import { formatBytes, formatDay } from "../portalModel";
import { refreshClientPortal, useClientPortalDocuments } from "../queries";
import type { PortalDocument, UploadCheck } from "../types";
import { PortalLoadError } from "./PortalHomePage";

const openDocument = async (clientId: string, doc: PortalDocument) => {
  const tab = window.open("", "_blank");
  const blob = await ForeignNationalDocumentsAPI.getFileBlob(clientId, doc.id);
  const url = URL.createObjectURL(
    blob.type ? blob : new Blob([blob], { type: doc.content_type })
  );
  if (tab) tab.location.href = url;
  else window.location.assign(url);
};

const DocumentRow: FC<{
  doc: PortalDocument;
  clientId: string;
  preview: boolean;
}> = ({ doc, clientId, preview }) => {
  const { t, i18n } = usePortalT();
  const [busy, setBusy] = useState(false);
  const remove = () => {
    setBusy(true);
    ForeignNationalDocumentsAPI.remove(clientId, doc.id)
      .then(() => {
        toast.success(t("documents.removed", { name: doc.filename }));
        return refreshClientPortal();
      })
      .catch(() => toast.error(t("documents.removeFailed")))
      .finally(() => setBusy(false));
  };
  return (
    <li
      className="flex items-center gap-3 border-t border-line-2 py-3 first:border-t-0 first:pt-0"
      data-testid="portal-document"
    >
      <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
        <FileText className="size-4" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-sm font-medium">
          {doc.label ?? doc.filename}
        </span>
        <span className="truncate text-xs text-muted-foreground">
          {[
            doc.from === "you"
              ? t("documents.fromYou")
              : t("documents.fromTeam"),
            formatDay(doc.created_at, i18n.language),
            formatBytes(doc.size_bytes)
          ]
            .filter(Boolean)
            .join(" · ")}
        </span>
      </span>
      <Button
        size="sm"
        variant="outline"
        onClick={() =>
          void openDocument(clientId, doc).catch(() =>
            toast.error(t("shell.loadFailed"))
          )
        }
      >
        {t("documents.open")}
      </Button>
      {doc.from === "you" && !preview ? (
        <Button
          size="icon-sm"
          variant="ghost"
          aria-label={t("documents.remove")}
          disabled={busy}
          onClick={remove}
        >
          <Trash2 />
        </Button>
      ) : null}
    </li>
  );
};

type Checked = { id: string; name: string; check?: UploadCheck };

const CHECKABLE = /^(image\/|application\/pdf$)/;

const useUpload = (clientId: string) => {
  const { t } = usePortalT();
  const [uploading, setUploading] = useState<string | null>(null);
  const [checks, setChecks] = useState<Checked[]>([]);

  const settle = (id: string, check: UploadCheck) =>
    setChecks((prev) =>
      check.checked
        ? prev.map((c) => (c.id === id ? { ...c, check } : c))
        : prev.filter((c) => c.id !== id)
    );

  const upload = async (files: File[]) => {
    for (const file of files) {
      setUploading(file.name);
      try {
        // Sequential on purpose: phones on slow networks choke on parallel uploads.
        // eslint-disable-next-line no-await-in-loop
        const doc = await ForeignNationalDocumentsAPI.upload(clientId, file);
        toast.success(t("documents.uploaded", { name: file.name }));
        const id = doc.foreign_national_document_id;
        if (CHECKABLE.test(file.type) && id) {
          setChecks((prev) => [{ id, name: file.name }, ...prev]);
          ClientPortalAPI.checkDocument(id)
            .then((check) => settle(id, check))
            .catch(() => settle(id, { checked: false }));
        }
      } catch {
        toast.error(t("documents.uploadFailed", { name: file.name }));
      }
    }
    setUploading(null);
    await refreshClientPortal();
  };
  const dismiss = (id: string) =>
    setChecks((prev) => prev.filter((c) => c.id !== id));
  return { upload, uploading, checks, dismiss };
};

export const PortalDocumentsPage: FC = () => {
  const me = useClientPortal();
  const { t } = usePortalT();
  const docs = useClientPortalDocuments();
  const { upload, uploading, checks, dismiss } = useUpload(me.client.uid);
  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);

  // The picker has to open inside the click, so remove the old file alongside.
  const replace = (id: string) => {
    fileInput.current?.click();
    dismiss(id);
    ForeignNationalDocumentsAPI.remove(me.client.uid, id)
      .then(() => refreshClientPortal())
      .catch(() => toast.error(t("documents.removeFailed")));
  };

  const onFiles = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    e.target.value = "";
    if (files.length) void upload(files);
  };

  const uploadDisabled = me.preview || Boolean(uploading);
  return (
    <>
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <span className="flex flex-1 flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight">
            {t("documents.title")}
          </h1>
          <span className="text-md text-muted-foreground">
            {t("documents.subtitle")}
          </span>
        </span>
        <span className="flex gap-2">
          <Button
            variant="primary"
            className="flex-1 sm:flex-none"
            disabled={uploadDisabled}
            title={me.preview ? t("documents.previewOnly") : undefined}
            onClick={() => fileInput.current?.click()}
            data-testid="portal-upload"
          >
            <Upload />
            {t("documents.upload")}
          </Button>
          <Button
            variant="outline"
            className="flex-1 sm:hidden"
            disabled={uploadDisabled}
            onClick={() => cameraInput.current?.click()}
          >
            <Camera />
            {t("documents.takePhoto")}
          </Button>
        </span>
        <input
          ref={fileInput}
          type="file"
          multiple
          hidden
          accept={CLIENT_DOCUMENT_ACCEPT}
          onChange={onFiles}
        />
        <input
          ref={cameraInput}
          type="file"
          hidden
          accept="image/*"
          capture="environment"
          onChange={onFiles}
        />
      </header>
      {uploading ? (
        <p className="text-sm text-muted-foreground" role="status">
          {t("documents.uploading", { name: uploading })}
        </p>
      ) : null}
      {checks.map((c) => (
        <UploadCheckCard
          key={c.id}
          name={c.name}
          check={c.check}
          actions={
            c.check ? (
              <>
                {c.check.checked && !c.check.ok ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => replace(c.id)}
                  >
                    <RotateCcw />
                    {t("documents.replace")}
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => dismiss(c.id)}
                >
                  {t("documents.keep")}
                </Button>
              </>
            ) : null
          }
        />
      ))}
      {docs.isLoading ? <LoadingState size="lg" /> : null}
      {!docs.isLoading && !docs.data ? (
        <PortalLoadError onRetry={() => void docs.refetch()} />
      ) : null}
      {docs.data ? (
        <PortalCard>
          {docs.data.length ? (
            <ul className="flex flex-col">
              {docs.data.map((doc) => (
                <DocumentRow
                  key={doc.id}
                  doc={doc}
                  clientId={me.client.uid}
                  preview={me.preview}
                />
              ))}
            </ul>
          ) : (
            <Empty className="p-6">
              <EmptyHeader>
                <EmptyMedia variant="icon">
                  <FileText />
                </EmptyMedia>
                <EmptyTitle>{t("documents.none")}</EmptyTitle>
                <EmptyDescription>{t("documents.noneBody")}</EmptyDescription>
              </EmptyHeader>
            </Empty>
          )}
        </PortalCard>
      ) : null}
    </>
  );
};
